import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import { SanityProduct } from "@graphql-types";
import { useStore } from "@state/store";
import { animationTypes, MOBILE_BREAKPOINT, TABLET_BREAKPOINT, TWENTY_INCH_SCREEN } from "@util/constants";
import { useCheckScreenWidth } from "@util/hooks";
import { addItemToCart, getProduct } from "@util/shopifyHelper";
import { Container, P, A } from "@util/standard";
import AnimationContainer from "./animationContainer";
import Button from "./button";
import CartSuccessToast from "./cartSuccessToast";
import Image from "./image";

interface Props {
  data: SanityProduct;
  margin?: string;
}

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  img {
    min-height: 400px;
    aspect-ratio: 4 / 3;

    @media only screen and (min-width: ${TWENTY_INCH_SCREEN}px) {
      height: 550px;
    }

    @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
      min-height: 300px;
    }
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      min-height: 230px;
    }
  }
`;

const Price = styled(P) <{ sale?: boolean }>`
  ${({ sale }) =>
    sale &&
    `text-decoration: line-through;
    opacity: 0.4;`};
  margin-right: 20px;
`;

function ProductCard({ data, margin }: Props) {
  const [cartButtonVisible, setCartButtonVisible] = useState(false);
  const { isLaptopWidth } = useCheckScreenWidth();
  const { checkout } = useStore();

  useEffect(() => {
    if (!isLaptopWidth) {
      setCartButtonVisible(false);
    }
  });

  if (!data) return null;

  const handleClick = async () => {
    if (checkout) {
      getProduct(data)
        .then(product => {
          if (!product || product.length === 0) {
            toast.error("Product not found.");
            return;
          }

          const productData = product[0];
          if (!productData.variants || !productData.variants.edges || productData.variants.edges.length === 0) {
            toast.error("Product variant not found.");
            return;
          }

          const variantId = productData.variants.edges[0].node.id;
          addItemToCart(checkout.id, variantId, 1);
          toast.success(CartSuccessToast);
        })
        .catch(error => {
          console.error("Error adding item to cart:", error);
          toast.error(`Something went wrong when adding Item to Cart`);
        });
    }
  };

  return (
    <Container
      flexDirection="column"
      cursor="pointer"
      width="100%"
      height="100%"
      margin={margin}
      onMouseEnter={() => setCartButtonVisible(true)}
      onMouseLeave={() => setCartButtonVisible(false)}
    >
      <Container>
        {data.productImages && data.productImages[0] && (
          <A href={`/products/${data.slug?.current}`}>
            <ImageWrapper>
              <Image objectFit="cover" width="100%" image={data.productImages[0]?.asset?.gatsbyImageData} imageID={data.productImages[0]?.asset?._id} quality={180} />
            </ImageWrapper>
          </A>
        )}
      </Container>
      <Container flexDirection="column" height="57px">
        {!cartButtonVisible && (
          <P margin="20px 0 15px 0" color="black">
            {data.productName?.toUpperCase()}
          </P>
        )}
        {cartButtonVisible && (
          <AnimationContainer animationType={animationTypes.fadeIn}>
            <Button width="100%" onClick={() => handleClick()} theme="block" text="Add to cart" margin="16px 0" />
          </AnimationContainer>
        )}
        <Container>
          <Price noMargin sale={data.onSale as boolean}>
            ${data.price}
          </Price>
          {data.onSale && <P noMargin>${data.salePrice}</P>}
        </Container>
      </Container>
    </Container>
  );
}

export default ProductCard;
